<script setup lang="ts">
/**
 * PUT /connectors/{id}/credentials. Replaces the stored credentials wholesale; the server
 * verifies the candidate against the source before it keeps them, so a wrong secret is
 * refused here rather than discovered at the next sync. The connector's settings do not change.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Field from '~/components/ui/Field.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'

type S = components['schemas']
const props = defineProps<{ connector: { id: string; name: string; type: S['ConnectorType'] } }>()
const emit = defineEmits<{ close: []; submit: [payload: S['SetConnectorCredentialsRequest']] }>()
const f = reactive({ accessKeyId: '', secretAccessKey: '', user: '', password: '', authHeaderValue: '' })
const complete = computed(() => props.connector.type === 's3' ? Boolean(f.accessKeyId && f.secretAccessKey) : props.connector.type === 'api' ? Boolean(f.authHeaderValue) : Boolean(f.user && f.password))
function payload(): S['SetConnectorCredentialsRequest'] {
  const type = props.connector.type
  if (type === 's3') return { type, credentials: { accessKeyId: f.accessKeyId, secretAccessKey: f.secretAccessKey } }
  if (type === 'api') return { type, credentials: { authHeaderValue: f.authHeaderValue } }
  return { type, credentials: { user: f.user, password: f.password } }
}
</script>

<template>
  <Dialog title="Rotate credentials" :meta="connector.name" :width="480" @close="emit('close')">
    <template #icon><Icon name="key" /></template>
    <p class="m-0 text-xs leading-relaxed text-muted-foreground">The new credentials are checked against the source before they replace the old ones. Datasets that read from this connector keep working without a change.</p>
    <template v-if="connector.type === 's3'">
      <Field v-model="f.accessKeyId" label="Access key id" autocomplete="off" required />
      <Field v-model="f.secretAccessKey" label="Secret access key" type="password" autocomplete="off" hint="Write-only · the current secret is never shown" required />
    </template>
    <Field v-else-if="connector.type === 'api'" v-model="f.authHeaderValue" label="Header value" type="password" autocomplete="off" hint="The full value of the auth header" required />
    <template v-else>
      <Field v-model="f.user" label="User" required />
      <Field v-model="f.password" label="Password" type="password" autocomplete="off" hint="Write-only · the current secret is never shown" required />
    </template>
    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton label="Replace credentials" variant="primary" :disabled="!complete" @click="emit('submit', payload())"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
